import { mockLegalConsentsProps } from '../organisms/LegalConsents/__mock__/LegalConsents.mock';
import {
  ConsentError,
  ConsentErrorType,
  ConsentsSectionTypes,
  ConsentsState,
} from '../organisms/LegalConsents/ConsentsList/ConsentsList.types';
import {
  ConsentCheckbox,
  ConsentHeader,
  ConsentSectionType,
  ConsentsLedger,
  LegalConsent,
} from '../organisms/LegalConsents/LegalConsents.types';

export function toggleSelectedConsentCheckbox({
  consentState,
  selectedCheckboxId,
}: {
  consentState: ConsentsState;
  selectedCheckboxId: string;
}): ConsentsState {
  const { consentsLedger, list: consents } = consentState;
  const isAgreeAllConsentsInTheList = !!consentsLedger[ConsentSectionType.AgreeToAll];
  const isRequiredConsentsInTheList = !!consentsLedger[ConsentSectionType.Required];
  const [selectedConsentsSectionType, selectedCheckboxName] = selectedCheckboxId.split('-');
  const selectedConsentsSectionTypeIsAgreeAll = !!(selectedConsentsSectionType === ConsentSectionType.AgreeToAll);

  // Get the current value of the selected checkbox and toggle it
  const selectedConsentCheckboxCurrentValue = consentsLedger[selectedConsentsSectionType][selectedCheckboxName];
  const toggledSelectedConsentCheckboxValue = !selectedConsentCheckboxCurrentValue;

  // Check if the selected checkbox is the "Agree to All" checkbox
  const isToggleAllTheConsentsCheckboxes = selectedConsentsSectionTypeIsAgreeAll;
  let isAllRequiredConsetsChecked = true;
  let isAllCheckboxesChecked = true;
  let agreeAllConsents: LegalConsent | null = null;

  for (const consent of consents) {
    const { checkboxes, sectionType } = consent;
    // Identify the "Agree to All" consent section
    if (isAgreeAllConsentsInTheList && sectionType === ConsentSectionType.AgreeToAll) {
      agreeAllConsents = consent;
    }

    for (const checkbox of checkboxes) {
      // Toggle the checkbox if it's the selected one or if toggling all consents
      if (isToggleAllTheConsentsCheckboxes || checkbox.name === selectedCheckboxName) {
        checkbox.checked = toggledSelectedConsentCheckboxValue;
        consentsLedger[sectionType][checkbox.name] = toggledSelectedConsentCheckboxValue;
      }
      // Check if all checkboxes are checked, excluding the "Agree to All" section
      if (!checkbox.checked && sectionType !== ConsentSectionType.AgreeToAll) {
        isAllCheckboxesChecked = false;
      }
      if (sectionType === ConsentSectionType.Required && !checkbox.checked) {
        isAllRequiredConsetsChecked = false;
      }
    }
  }

  // If all checkboxes are checked, update the "Agree to All" checkbox accordingly
  if (isAgreeAllConsentsInTheList && agreeAllConsents) {
    const agreeAllConsentsCheckboxValue = isAllCheckboxesChecked;
    const agreeAllConsentsCheckbox = agreeAllConsents.checkboxes[0];
    agreeAllConsentsCheckbox.checked = agreeAllConsentsCheckboxValue;
    consentsLedger[agreeAllConsents.sectionType][agreeAllConsentsCheckbox.name] = agreeAllConsentsCheckboxValue;
  }

  // Update the error state based on the required consents
  if (isRequiredConsentsInTheList) {
    if (selectedConsentsSectionTypeIsAgreeAll) {
      consentState.error.type = toggledSelectedConsentCheckboxValue
        ? ConsentErrorType.NONE
        : ConsentErrorType.ALL_REQUIRED_CONSENTS_NOT_SELECTED;
    } else {
      consentState.error.type = isAllRequiredConsetsChecked
        ? ConsentErrorType.NONE
        : ConsentErrorType.ALL_REQUIRED_CONSENTS_NOT_SELECTED;
    }
  }
  return consentState;
}

export function getApplicableAndSortedConsents({
  allConsents,
  preStateLedger = {},
  countryCode,
  option,
}: {
  allConsents: LegalConsent[];
  preStateLedger?: ConsentsLedger;
  countryCode: string;
  option: string;
}): ConsentsState {
  let filteredConsents: LegalConsent[] = [];
  let sectionTypes: ConsentsSectionTypes = {};
  let isAgreeAllConsentCheckboxPreChecked = false;
  const ledger = {} as ConsentsLedger;

  const response: ConsentsState = {
    list: [],
    sectionTypes: {},
    consentsLedger: ledger,
    error: { type: ConsentErrorType.NONE, message: null },
  };

  const validateAndFilterConsentsListResponse = validateAndFilterConsentsList({
    allConsents,
    countryCode,
    option,
    sortEntries: true,
  });

  if (validateAndFilterConsentsListResponse.error.type !== ConsentErrorType.NONE) {
    response.error = validateAndFilterConsentsListResponse.error;
    return response;
  }

  filteredConsents = validateAndFilterConsentsListResponse.filteredList;
  sectionTypes = validateAndFilterConsentsListResponse.sectionTypes;
  if (sectionTypes[ConsentSectionType.AgreeToAll]) {
    const agreeAllConsentCheckboxes =
      validateAndFilterConsentsListResponse[ConsentSectionType.AgreeToAll]?.checkboxes?.[0];
    isAgreeAllConsentCheckboxPreChecked = agreeAllConsentCheckboxes?.defaultChecked ? true : false;
  }

  for (const consent of filteredConsents) {
    const { headers, checkboxes } = consent;

    const filteredHeaders = getApplicableAndSortedConsentHeaders({
      headers,
      countryCode,
      option,
      sortEntries: true,
    });

    const { filteredCheckboxes, isAllTheCheckboxesSelected } = getApplicableAndSortedConsentCheckboxes({
      checkboxes,
      preStateLedger,
      ledger,
      countryCode,
      consentErrorMessage: consent.errorMessage,
      sectionType: consent.sectionType as ConsentSectionType,
      option,
      isAgreeAllConsentCheckboxPreChecked,
      sortEntries: true,
    });

    // If required consents are not selected then error state will be updated with ConsentsError.ALL_REQUIRED_CONSENTS_NOT_SELECTED
    if (consent.sectionType === ConsentSectionType.Required && !isAllTheCheckboxesSelected) {
      response.error.type = ConsentErrorType.ALL_REQUIRED_CONSENTS_NOT_SELECTED;
    }

    consent.headers = filteredHeaders;
    consent.checkboxes = filteredCheckboxes;
  }

  response.list = filteredConsents;
  response.sectionTypes = sectionTypes;
  response.consentsLedger = ledger;
  return response;
}

function getApplicableAndSortedConsentHeaders({
  headers,
  countryCode,
  option,
  sortEntries = false,
}: {
  headers: ConsentHeader[];
  countryCode: string;
  option: string;
  sortEntries?: boolean;
}): ConsentHeader[] {
  let filteredHeaders = [] as ConsentHeader[];

  if (headers.length < 1) {
    return headers;
  }

  for (const header of headers) {
    const { forCountries, excludeCountries, forOptions, excludeOptions } = header;

    const isHeaderToBeSkipped = checkIfElementToBeSkippedByCountryAndOptions({
      countryCode,
      option,
      forCountries,
      excludeCountries,
      forOptions,
      excludeOptions,
    });

    // Skipping the entry if the conditions are not met
    if (isHeaderToBeSkipped) {
      continue;
    }

    filteredHeaders.push({ ...header });
  }

  if (sortEntries && filteredHeaders.length > 1) {
    filteredHeaders = sortArrayByField({ data: filteredHeaders, sortField: 'index' });
  }

  return filteredHeaders;
}

function getApplicableAndSortedConsentCheckboxes({
  checkboxes,
  preStateLedger,
  ledger,
  sectionType,
  countryCode,
  option,
  sortEntries = false,
  isAgreeAllConsentCheckboxPreChecked,
  consentErrorMessage,
}: {
  checkboxes: ConsentCheckbox[];
  preStateLedger: ConsentsLedger;
  ledger: ConsentsLedger;
  sectionType: ConsentSectionType;
  countryCode: string;
  option: string;
  sortEntries?: boolean;
  isAgreeAllConsentCheckboxPreChecked: boolean;
  consentErrorMessage: string | null;
}) {
  let filteredCheckboxes: ConsentCheckbox[] = [];
  let isAllTheCheckboxesSelected = true;

  for (const _checkbox of checkboxes) {
    const checkbox = { ..._checkbox };
    const { forCountries, excludeCountries, forOptions, excludeOptions } = checkbox;

    const isCheckboxToBeSkipped = checkIfElementToBeSkippedByCountryAndOptions({
      countryCode,
      option,
      forCountries,
      excludeCountries,
      forOptions,
      excludeOptions,
    });

    // Skipping the entry if the conditions are not met
    if (isCheckboxToBeSkipped) {
      continue;
    }

    checkbox.id = `${sectionType}-${checkbox.name}`;
    const _preStateCheckboxValue = preStateLedger?.[sectionType]?.[checkbox.name];
    const preStateCheckboxValue =
      _preStateCheckboxValue === true ? true : _preStateCheckboxValue === false ? false : null;

    const isChecked =
      preStateCheckboxValue !== null
        ? preStateCheckboxValue
        : isAgreeAllConsentCheckboxPreChecked || !!checkbox.defaultChecked;
    checkbox.checked = isChecked;
    if (!checkbox.errorMessage && consentErrorMessage) {
      checkbox.errorMessage = consentErrorMessage;
    }

    filteredCheckboxes.push({ ...checkbox });

    if (!isChecked) {
      isAllTheCheckboxesSelected = false;
    }

    // ledger passed as reference so updating will not require re-assigning
    if (ledger[sectionType]) {
      ledger[sectionType][checkbox.name] = checkbox.checked;
    } else {
      ledger[sectionType] = { [checkbox.name]: checkbox.checked };
    }
  }

  if (sortEntries && filteredCheckboxes.length > 1) {
    filteredCheckboxes = sortArrayByField({ data: filteredCheckboxes, sortField: 'index' });
  }

  return {
    filteredCheckboxes,
    isAllTheCheckboxesSelected,
    ledger,
  };
}

// Returns true or false based in the input
export function isEmpty(value: unknown): boolean {
  if (value === null || value === undefined || value === '') {
    return true;
  }
  if (Array.isArray(value) && value.length === 0) {
    return true;
  }
  if (typeof value === 'object' && Object.keys(value).length === 0) {
    return true;
  }
  return false;
}

function sortArrayByField<T, U extends keyof T>({ data, sortField }: { data: Array<T>; sortField: U }) {
  data = data?.sort((a, b) => {
    if (a[sortField] < b[sortField]) {
      return -1;
    }
    if (a[sortField] > b[sortField]) {
      return 1;
    }
    return 0;
  });

  return data;
}

export async function getLegalConsents() {
  return mockLegalConsentsProps;
}

function checkIfElementToBeSkippedByCountryAndOptions({
  countryCode,
  option,
  forCountries,
  excludeCountries,
  forOptions,
  excludeOptions,
}: {
  countryCode: string;
  option: string;
  forCountries: string[] | null;
  excludeCountries: string[] | null;
  forOptions: string[] | null;
  excludeOptions: string[] | null;
}) {
  const isValueInList = (list: string[] | null, value: string, shouldBeInTheList: boolean): boolean => {
    // Always check for isEmpty first
    if (list && list.length > 0) {
      return shouldBeInTheList ? !list.includes(value) : list.includes(value);
    }
    return false;
  };

  // If forCountries field does not exists or empty
  // Then considering that by default it is applied for all the country code that are tagged at the consent section level
  // If forCountries has the values and it does not match with the current country code then we have to skip this checkbox entry
  if (isValueInList(forCountries, countryCode, true) || isValueInList(excludeCountries, countryCode, false)) {
    return true;
  }

  // If forOptions field does not exists or empty
  // Then considering that by default it is applied for all the options that are tagged at the consent section level
  // If forOptions has the values and it does not match with the current country code then we have to skip this checkbox entry
  if (isValueInList(forOptions, option, true) || isValueInList(excludeOptions, option, false)) {
    return true;
  }

  return false;
}

const ErrorMessages = {
  AGREE_ALL_CHECKBOXES: `Internal Validation Error: AgreeAll consents cannot have more than one checkbox, please ensure there is only one checkbox entry present.`,
  AT_LEAST_ONE_FIELD_PRESENT: (fieldsList: string[], context: string, name: string) =>
    `Internal Validation Error: Invalid entry found for '${name}', At least one of the following properties must be present in '${context}': ${fieldsList.join(
      ', '
    )}`,
  EMPTY_CONSENT: `Internal Validation Error: Consent entry cannot be empty, please ensure consents has all required fields present on the object.`,
  EMPTY_COUNTRIES: (name: string) =>
    `Internal Validation Error: Property 'countries' in 'Consent' for '${name}' cannot be empty, please ensure all the mandatory fields are filled with valid values/data.`,
  EMPTY_FIELD: (field: string, context: string, name: string) =>
    `Internal Validation Error: Property '${field}' in '${context}' for '${name}' cannot be empty, please ensure all the mandatory fields are filled with valid values/data.`,
  EMPTY_OPTIONS: (name: string) =>
    `Internal Validation Error: Property 'options' in 'Consent' for '${name}' cannot be empty, please ensure all the mandatory fields are filled with valid values/data.`,
  TERMS_LINK: (name: string) =>
    `Internal Validation Error: Property 'termsLink' for '${name}' cannot be empty, please ensure 'termsLink' is filled with valid values/data.`,
  TERMS_LINK_LABEL: (name: string) =>
    `Internal Validation Error: Property 'termsLinkLabel' for '${name}' cannot be empty, please ensure 'termsLinkLabel' is filled with valid values/data.`,
};

function generateValidationError(message: string): ValidationError {
  return {
    type: ConsentErrorType.INTERNAL_VALIDATION_ERROR,
    message,
  };
}

interface ValidateAndFilterConsentsListParams {
  allConsents: LegalConsent[];
  countryCode: string;
  option: string;
  sortEntries: true;
}
interface ValidateAndFilterConsentsListResponse {
  filteredList: LegalConsent[];
  error: ConsentError;
  sectionTypes: ConsentsSectionTypes;
  [ConsentSectionType.AgreeToAll]: LegalConsent;
}

function validateAndFilterConsentsList({
  allConsents,
  countryCode,
  option,
  sortEntries,
}: ValidateAndFilterConsentsListParams): ValidateAndFilterConsentsListResponse {
  let applicableConsents = [] as LegalConsent[];
  const sectionTypes: ConsentsSectionTypes = {};
  const reponse: ValidateAndFilterConsentsListResponse = {
    filteredList: [],
    error: { type: ConsentErrorType.NONE, message: null },
    sectionTypes: {},
    [ConsentSectionType.AgreeToAll]: {} as LegalConsent,
  };

  for (const consent of allConsents) {
    if (isEmpty(consent)) {
      reponse.error = generateValidationError(ErrorMessages.EMPTY_CONSENT);
      return reponse;
    }

    const { name, countries, options } = consent;

    if (isEmpty(countries)) {
      reponse.error = generateValidationError(ErrorMessages.EMPTY_COUNTRIES(name));
      return reponse;
    }

    if (isEmpty(options)) {
      reponse.error = generateValidationError(ErrorMessages.EMPTY_OPTIONS(name));
      return reponse;
    }

    const isCountryMatched = countries.indexOf(countryCode) !== -1;
    const isOptionMatched = options.indexOf(option) !== -1;
    if (isCountryMatched && isOptionMatched) {
      sectionTypes[consent.sectionType] = consent.sectionType;
      const validationErrorForConsent = ValidateAllMandatoryFieldsArePresentForConsent({ consent });

      // Return with error state if invalid format or missing fields found
      if (validationErrorForConsent.type !== ConsentErrorType.NONE) {
        reponse.error = validationErrorForConsent;
        return reponse;
      }

      const headers = consent.headers;
      if (!isEmpty(headers)) {
        for (const header of headers) {
          const validationErrorForConsentHeader = ValidateAllMandatoryFieldsArePresentForConsentHeader({
            header,
          });

          if (validationErrorForConsentHeader.type !== ConsentErrorType.NONE) {
            reponse.error = validationErrorForConsentHeader;
            return reponse;
          }
        }
      }

      const checkboxes = consent.checkboxes;
      if (!isEmpty(checkboxes)) {
        for (const checkbox of checkboxes) {
          const validationErrorForConsentCheckbox = ValidateAllMandatoryFieldsArePresentForConsentCheckbox({
            checkbox,
          });

          if (validationErrorForConsentCheckbox.type !== ConsentErrorType.NONE) {
            reponse.error = validationErrorForConsentCheckbox;
            return reponse;
          }
        }
      }

      if (consent.sectionType === ConsentSectionType.AgreeToAll) {
        reponse[ConsentSectionType.AgreeToAll] = consent;
      }
      applicableConsents.push({ ...consent });
    }
  }

  if (sortEntries) {
    applicableConsents = sortArrayByField({ data: applicableConsents, sortField: 'index' });
  }
  reponse.filteredList = applicableConsents;
  reponse.sectionTypes = sectionTypes;
  return reponse;
}

interface ValidationError {
  type: ConsentErrorType;
  message: string | null;
}

interface ValidationParams {
  fields: string[];
  data: LegalConsent | ConsentHeader | ConsentCheckbox;
  context: string;
}

function validateMandatoryFields({
  fields,
  data,
  context,
  name,
}: ValidationParams & { name: string }): ValidationError {
  for (const field of fields) {
    const value = data[field as keyof typeof data];
    if (isEmpty(value)) {
      return generateValidationError(ErrorMessages.EMPTY_FIELD(field, context, name));
    }
  }
  return { type: ConsentErrorType.NONE, message: null };
}

function validateAtLeastOneFieldPresent({
  fields,
  data,
  context,
  name,
}: ValidationParams & { name: string }): ValidationError {
  for (const field of fields) {
    if (!isEmpty(data[field as keyof typeof data])) {
      return { type: ConsentErrorType.NONE, message: null };
    }
  }
  return generateValidationError(ErrorMessages.AT_LEAST_ONE_FIELD_PRESENT(fields, context, name));
}

export const mandatoryFieldsForConsents = ['id', 'name', 'countries', 'options', 'sectionType', 'checkboxes'];
function ValidateAllMandatoryFieldsArePresentForConsent({ consent }: { consent: LegalConsent }): ValidationError {
  const validationError = validateMandatoryFields({
    fields: mandatoryFieldsForConsents,
    data: consent,
    context: 'Consent',
    name: consent.name,
  });

  // Return error if validation of mandatory fields fails
  if (validationError.type !== ConsentErrorType.NONE) {
    return validationError;
  }

  const isSectionTypeIsAgreeAll = consent.sectionType === ConsentSectionType.AgreeToAll;
  const hasMultipleCheckboxes = consent.checkboxes.length > 1;
  // AgreeToAll section type should have only one checkbox
  if (isSectionTypeIsAgreeAll && hasMultipleCheckboxes) {
    return generateValidationError(ErrorMessages.AGREE_ALL_CHECKBOXES);
  }

  return validationError;
}

export const MandatoryFieldsForHeader = ['id', 'name'];
function ValidateAllMandatoryFieldsArePresentForConsentHeader({ header }: { header: ConsentHeader }): ValidationError {
  let error = validateMandatoryFields({
    fields: MandatoryFieldsForHeader,
    data: header,
    context: 'ConsentHeader',
    name: header.name,
  });
  if (error.type !== ConsentErrorType.NONE) return error;

  const LeastOneFieldPresentForHeader = ['header', 'subHeader', 'description'];
  error = validateAtLeastOneFieldPresent({
    fields: LeastOneFieldPresentForHeader,
    data: header,
    context: 'ConsentHeader',
    name: header.name,
  });
  return error;
}

export const MandatoryFieldsForCheckbox = ['id', 'name', 'isDisabled', 'defaultChecked', 'showCheckbox', 'label'];
function ValidateAllMandatoryFieldsArePresentForConsentCheckbox({
  checkbox,
}: {
  checkbox: ConsentCheckbox;
}): ValidationError {
  const error = validateMandatoryFields({
    fields: MandatoryFieldsForCheckbox,
    data: checkbox,
    context: 'ConsentCheckbox',
    name: checkbox.name,
  });
  if (error.type !== ConsentErrorType.NONE) return error;

  const { termsLink, termsLinkLabel } = checkbox;
  if (termsLink && !termsLinkLabel) {
    return generateValidationError(ErrorMessages.TERMS_LINK_LABEL(checkbox.name));
  }
  if (termsLinkLabel && !termsLink) {
    return generateValidationError(ErrorMessages.TERMS_LINK(checkbox.name));
  }
  return { type: ConsentErrorType.NONE, message: null };
}
